import React, { useState } from 'react';
import { BarChart3, Users, TrendingUp, AlertTriangle, Target, Settings, Brain, Shield } from 'lucide-react';
import { 
  AdminPlatformAnalysis,
  AdminOperationRecommendations,
  AdminUserAnalysis,
  AdminRevenueAnalysis,
  AdminRiskAnalysis,
  AdminSystemOptimization
} from '../../components/AI/Admin/AdminPlatformAnalysis';

type ActiveTab = 'platform' | 'operations' | 'users' | 'revenue' | 'risks' | 'optimization';

export const AdminAIPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('platform');

  const tabs = [
    {
      id: 'platform' as const,
      label: '平台分析',
      icon: BarChart3,
      description: 'AI全面分析平台数据，洞察业务健康度'
    },
    {
      id: 'operations' as const,
      label: '运营建议',
      icon: Target,
      description: '基于数据分析提供精准的运营策略建议'
    },
    {
      id: 'users' as const,
      label: '用户分析',
      icon: Users,
      description: '深度分析用户行为，提升用户体验和留存'
    },
    {
      id: 'revenue' as const,
      label: '收入分析',
      icon: TrendingUp,
      description: '收入趋势分析，发现增长机会和优化点'
    },
    {
      id: 'risks' as const,
      label: '风险预警',
      icon: AlertTriangle,
      description: '智能识别潜在风险，提供预防和应对方案'
    },
    {
      id: 'optimization' as const,
      label: '系统优化',
      icon: Settings,
      description: '系统性能分析和优化建议，提升平台效率'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'platform':
        return <AdminPlatformAnalysis />;
      case 'operations':
        return <AdminOperationRecommendations />;
      case 'users':
        return <AdminUserAnalysis />;
      case 'revenue':
        return <AdminRevenueAnalysis />;
      case 'risks':
        return <AdminRiskAnalysis />;
      case 'optimization':
        return <AdminSystemOptimization />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-3 mb-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Shield className="h-8 w-8" />
              </div>
              <h1 className="text-4xl font-bold">管理员AI控制台</h1>
            </div>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              专为平台管理员打造的AI商业智能中心，提供全方位数据分析、风险预警和运营优化
            </p>
          </div>
          
          {/* 功能特色 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                icon: BarChart3,
                title: '数据洞察',
                description: '实时监控平台各项核心指标，AI自动识别异常和趋势'
              },
              {
                icon: Target,
                title: '智能决策',
                description: '基于大数据分析提供科学的运营决策和策略建议'
              },
              {
                icon: AlertTriangle,
                title: '风险管控',
                description: '智能风险识别和预警系统，确保平台稳定运营'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <feature.icon className="h-12 w-12 mx-auto mb-4 text-purple-200" />
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-purple-100 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 标签导航 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 标签说明 */}
      <div className="bg-purple-50 border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-purple-700 text-sm">
            {tabs.find(tab => tab.id === activeTab)?.description}
          </p>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
}; 